import React, { useState } from 'react';
import { Clock, Users, Layers } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { DayShift, Employee } from '@/types';
import {
  safeParseDate,
  safeFormat,
  calculateTotalDuration,
  formatDuration,
  formatShiftTimeRange
} from '@/lib/shifts/template-utils';
import BaseTemplateDialog from './shared/BaseTemplateDialog';

interface SaveShiftTemplateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string, description?: string) => void;
  selectedShifts: DayShift[];
  employees: Employee[];
}

const SaveShiftTemplateDialog: React.FC<SaveShiftTemplateDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  selectedShifts,
  employees,
}) => {
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async (name: string, description?: string) => {
    setIsSaving(true);
    try {
      onSave(name, description);
    } finally {
      setIsSaving(false);
    }
  };

  // Get employee name for a shift
  const getEmployeeName = (employeeId: string) => {
    const employee = employees.find(emp => emp.id === employeeId);
    return employee?.name || 'Unassigned';
  };

  // Calculate total duration and format
  const totalDuration = calculateTotalDuration(selectedShifts);
  const formattedDuration = formatDuration(totalDuration);

  return (
    <BaseTemplateDialog
      isOpen={isOpen}
      onClose={onClose}
      onSave={handleSave}
      title="Save Shift Templates"
      icon={Layers}
      saveButtonText={`Save ${selectedShifts.length} Template${selectedShifts.length !== 1 ? 's' : ''}`}
      isLoading={isSaving}
    >
      {/* Template Preview */}
      <div className="text-sm max-h-48 overflow-y-auto">
        <div className="font-medium mb-2 flex items-center gap-2">
          <Clock className="h-4 w-4" />
          Template Preview ({selectedShifts.length} shift{selectedShifts.length !== 1 ? 's' : ''}) - Total: {formattedDuration}
        </div>
        <div className="space-y-2">
          {selectedShifts.map((shift) => {
            const startTime = safeParseDate(shift.start_time);
            const endTime = safeParseDate(shift.end_time);
            const duration = Math.round((endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60 * 100)) / 100;

            return (
              <Card key={shift.id} className="p-2">
                <CardContent className="p-0">
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span className="font-mono">
                        {formatShiftTimeRange(shift)}
                      </span>
                      <span className="text-muted-foreground">
                        ({duration}h)
                      </span>
                    </div>
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <Users className="h-3 w-3" />
                      <span>{getEmployeeName(shift.employee_id)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
        <div className="mt-2 text-muted-foreground text-xs">
          Note: Each shift will be saved as a separate reusable template.
        </div>
      </div>
    </BaseTemplateDialog>
  );
};

export default SaveShiftTemplateDialog;